<template>
  <transition name="fade">
    <div v-if="visible" class="modal-overlay" @click.self="$emit('disagree')">
      <div class="modal-box">

        <div class="modal-header-custom">
          <h5 class="modal-title-custom">{{ title }}</h5>
          <button class="modal-close-btn" @click="$emit('disagree')">&times;</button>
        </div>

        <div class="modal-body-custom">
          <p class="agreement-intro">{{ intro }}</p>
          <ul class="agreement-list">
            <li v-for="(item, index) in terms" :key="index" v-html="item"></li>
          </ul>

          <!-- Checkbox confirmation -->
          <label class="checkbox-label" :class="{ 'checkbox-label--error': showError }">
            <span class="checkbox-wrap">
              <input type="checkbox" v-model="agreed" @change="showError = false" />
              <span class="checkbox-custom"></span>
            </span>
            <span class="checkbox-text">{{ checkboxLabel }}</span>
          </label>
          <p v-if="showError" class="checkbox-error">Please check the box to proceed.</p>
        </div>

        <div class="modal-footer-custom">
          <button class="btn btn-outline-secondary modal-action-btn" @click="handleDisagree">
            {{ disagreeLabel }}
          </button>
          <button class="btn btn-primary modal-action-btn" @click="handleAgree">
            {{ agreeLabel }}
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "AgreementModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Acknowledgement",
    },
    intro: {
      type: String,
      default: "Please read and accept the following terms before proceeding:",
    },
    terms: {
      type: Array,
      default: () => [],
    },
    agreeLabel: {
      type: String,
      default: "Agree & Proceed",
    },
    disagreeLabel: {
      type: String,
      default: "Disagree",
    },
  },
  emits: ["agree", "disagree"],

  data() {
    return {
      agreed: false,
      showError: false,
    };
  },

  watch: {
    // Reset checkbox whenever modal is reopened
    visible(val) {
      if (val) {
        this.agreed = false;
        this.showError = false;
      }
    },
  },
  computed: {
    checkboxLabel() {
        return this.$t('agreement_txt');
    },
  },
  methods: {
    handleAgree() {
      if (!this.agreed) {
        this.showError = true;
        return;
      }
      this.$emit("agree");
    },
    handleDisagree() {
      this.agreed = false;
      this.showError = false;
      this.$emit("disagree");
    },
  },
};
</script>

<style scoped>
/* ── Modal Overlay ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

/* ── Modal Box ── */
.modal-box {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: slideUp 0.25s ease;
}
@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

/* ── Modal Header ── */
.modal-header-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #067169;
  color: #fff;
}
.modal-title-custom {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}
.modal-close-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  opacity: 0.85;
  transition: opacity 0.15s;
}
.modal-close-btn:hover { opacity: 1; }

/* ── Modal Body ── */
.modal-body-custom {
  padding: 20px 24px 8px;
}
.agreement-intro {
  font-size: 0.9rem;
  color: #444;
  margin-bottom: 10px;
}
.agreement-list {
  padding-left: 18px;
  margin: 0 0 16px;
}
.agreement-list li {
  font-size: 0.875rem;
  color: #555;
  margin-bottom: 8px;
  line-height: 1.5;
}

/* ── Checkbox ── */
.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1.5px solid #e0e0e0;
  background: #f9f9f9;
  transition: border-color 0.15s, background 0.15s;
  margin-bottom: 6px;
}
.checkbox-label:hover {
  border-color: #067169;
  background: #f0faf9;
}
.checkbox-label--error {
  border-color: #e53935;
  background: #fff5f5;
}

.checkbox-wrap {
  position: relative;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
}
.checkbox-wrap input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
}
.checkbox-custom {
  display: block;
  width: 18px;
  height: 18px;
  border: 2px solid #b0bec5;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;
}
/* Checked state */
.checkbox-wrap input[type="checkbox"]:checked + .checkbox-custom {
  background: #067169;
  border-color: #067169;
}
.checkbox-wrap input[type="checkbox"]:checked + .checkbox-custom::after {
  content: '';
  display: block;
  width: 5px;
  height: 9px;
  border: 2px solid #fff;
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
  margin: 1px auto 0;
}
/* Focus ring */
.checkbox-wrap input[type="checkbox"]:focus + .checkbox-custom {
  outline: 2px solid #067169;
  outline-offset: 2px;
}

.checkbox-text {
  font-size: 0.85rem;
  color: #333;
  line-height: 1.5;
  user-select: none;
}
.checkbox-error {
  font-size: 0.78rem;
  color: #e53935;
  margin: 0 0 4px 2px;
}

/* ── Modal Footer ── */
.modal-footer-custom {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 20px;
  border-top: 1px solid #eee;
}
.modal-action-btn {
  min-width: 120px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

/* ── Fade transition ── */
.fade-enter-active,
.fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }

@media (max-width: 768px) {
  .modal-footer-custom {
    flex-direction: column-reverse;
  }
  .modal-action-btn {
    width: 100%;
  }
}
</style>


<i18n>
{
  "en": {
    "agreement_txt": "I hereby acknowledge that I have read and understood the information provided above."
  },
  "si": {
    "agreement_txt": "ඉහත සපයා ඇති තොරතුරු මා විසින් කියවා තේරුම් ගෙන ඇති බව මෙයින් ප්‍රකාශ කරමි."
  },
  "ta": {
    "agreement_txt": "மேலே கொடுக்கப்பட்டுள்ள தகவல்களை நான் வாசித்துப் புரிந்துகொண்டேன் என்பதை இத்தால் அறியத் தருகின்றேன்."
  }
}
</i18n>